import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CancelIcon from "@mui/icons-material/Cancel";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";
import { getExpenseSummary } from "../../../redux/expenseRelated/expenseHandle";
import { getSchoolIdForUser } from "../../../utils/userContext";

const TERMS = ["Term 1", "Term 2", "Term 3", "Full Year"];

const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

const METHOD_COLORS = {
    Cash: "#00897b",
    "Airtel Money": "#e65100",
    "TNM Mpamba": "#6a1b9a",
    "Bank Transfer": "#1565c0",
    Cheque: "#558b2f",
    Other: "#37474f",
};

function formatMonthTick(monthKey) {
    const [ys, ms] = String(monthKey || "").split("-");
    const y = parseInt(ys, 10);
    const m = parseInt(ms, 10);
    if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return String(monthKey);
    const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${labels[m - 1]} ${y}`;
}

function abbrevMWKTick(v) {
    if (v == null || !Number.isFinite(Number(v))) return "0";
    const n = Math.abs(Number(v));
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return String(Math.round(n));
}

function methodColor(name) {
    const k = String(name || "").trim();
    return METHOD_COLORS[k] || METHOD_COLORS.Other;
}

function KpiCard({ title, value, subValue, color, icon: Icon, loading }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            {loading ? (
                <>
                    <Skeleton width="60%" height={20} sx={{ mb: 1 }} />
                    <Skeleton width="80%" height={36} />
                </>
            ) : (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {title}
                        </Typography>
                        <Icon sx={{ color: `${color}.main`, fontSize: 22 }} />
                    </Stack>
                    <Typography variant="h6" fontWeight={800} color={`${color}.main`}>
                        {value}
                    </Typography>
                    {subValue != null ? (
                        <Typography variant="caption" color="text.secondary">
                            {subValue}
                        </Typography>
                    ) : null}
                </>
            )}
        </Paper>
    );
}

const ExpenseSummary = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { expenseSummary, loading } = useSelector((s) => s.expense);

    const schoolId = getSchoolIdForUser(currentUser);

    const y = new Date().getFullYear();
    const ACADEMIC_YEARS = useMemo(() => [`${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`], [y]);

    const [filterTerm, setFilterTerm] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [fetchStarted, setFetchStarted] = useState(false);

    const buildActiveFilters = () => {
        const f = {};
        if (filterTerm) f.term = filterTerm;
        if (filterYear) f.academicYear = filterYear;
        if (dateFrom) f.from = dateFrom;
        if (dateTo) f.to = dateTo;
        return f;
    };

    const applyFilters = () => {
        if (!schoolId) return;
        dispatch(getExpenseSummary(schoolId, buildActiveFilters()));
    };

    useEffect(() => {
        if (!schoolId) return;
        setFetchStarted(true);
        dispatch(getExpenseSummary(schoolId, {}));
    }, [dispatch, schoolId]);

    const s = expenseSummary;
    const byMonth = Array.isArray(s?.byMonth) ? s.byMonth : [];
    const byCategory = useMemo(
        () => (Array.isArray(s?.byCategory) ? s.byCategory : []),
        [s?.byCategory],
    );

    const pieData = useMemo(() => {
        const byPaymentMethod = Array.isArray(s?.byPaymentMethod) ? s.byPaymentMethod : [];
        return byPaymentMethod.map((item) => ({
            name: item.method || "Other",
            value: Number(item.total) || 0,
        }));
    }, [s?.byPaymentMethod]);

    const totalApproved = Number(s?.totalApproved) || 0;
    const totalTx = useMemo(() => {
        const byCategory = Array.isArray(s?.byCategory) ? s.byCategory : [];
        return byCategory.reduce((acc, r) => acc + (Number(r.count) || 0), 0);
    }, [s?.byCategory]);
    const totalCatSpend = useMemo(() => {
        const byCategory = Array.isArray(s?.byCategory) ? s.byCategory : [];
        return byCategory.reduce((acc, r) => acc + (Number(r.total) || 0), 0);
    }, [s?.byCategory]);

    const showData = Boolean(s) && !loading;
    const showEmptyState = fetchStarted && !loading && !s;
    const showReportBody = loading || Boolean(s);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                <Typography variant="h5" component="h1" fontWeight={600}>
                    Expense Reports
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => schoolId && dispatch(getExpenseSummary(schoolId, buildActiveFilters()))}
                    disabled={!schoolId || loading}
                >
                    Refresh
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }} flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Term</InputLabel>
                        <Select label="Term" value={filterTerm} onChange={(e) => setFilterTerm(e.target.value)}>
                            <MenuItem value="">
                                <em>All</em>
                            </MenuItem>
                            {TERMS.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Academic Year</InputLabel>
                        <Select label="Academic Year" value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                            <MenuItem value="">
                                <em>All</em>
                            </MenuItem>
                            {ACADEMIC_YEARS.map((yr) => (
                                <MenuItem key={yr} value={yr}>
                                    {yr}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        type="date"
                        label="Date From"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="Date To"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" onClick={applyFilters} disabled={!schoolId || loading}>
                        Apply
                    </Button>
                </Stack>
            </Paper>

            {showEmptyState && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: "center", mb: 3 }}>
                    <Typography color="text.secondary">Apply filters to view expense report</Typography>
                </Paper>
            )}

            {showReportBody && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiCard
                            title="Total Approved Spend"
                            value={showData ? formatMWK(s.totalApproved) : "—"}
                            color="success"
                            icon={MonetizationOnIcon}
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiCard
                            title="Pending Approval"
                            value={showData ? String(s.pendingCount ?? 0) : "—"}
                            subValue={showData ? formatMWK(s.totalPending) : null}
                            color="warning"
                            icon={HourglassEmptyIcon}
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiCard
                            title="Total Expenses Logged"
                            value={showData ? String(s.count ?? 0) : "—"}
                            color="primary"
                            icon={ReceiptIcon}
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiCard
                            title="Rejected"
                            value={showData ? formatMWK(s.totalRejected) : "—"}
                            color="error"
                            icon={CancelIcon}
                            loading={loading}
                        />
                    </Grid>
                </Grid>
            )}

            {showReportBody && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} lg={8}>
                        <Paper variant="outlined" sx={{ p: 2, height: 320 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                Monthly Expenditure Trend
                            </Typography>
                            {loading ? (
                                <Skeleton variant="rounded" width="100%" height={240} />
                            ) : (
                                <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={byMonth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="month" tickFormatter={formatMonthTick} tick={{ fontSize: 11 }} />
                                        <YAxis tickFormatter={abbrevMWKTick} tick={{ fontSize: 11 }} width={44} />
                                        <RechartsTooltip formatter={(val) => formatMWK(val)} labelFormatter={formatMonthTick} />
                                        <Bar dataKey="total" fill="#1976d2" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <Paper variant="outlined" sx={{ p: 2, height: 320, display: "flex", flexDirection: "column" }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                By Payment Method
                            </Typography>
                            {loading ? (
                                <Skeleton variant="rounded" width="100%" height={240} sx={{ flex: 1 }} />
                            ) : pieData.length === 0 ? (
                                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No approved spend by method
                                    </Typography>
                                </Box>
                            ) : (
                                <ResponsiveContainer width="100%" height={240}>
                                    <PieChart>
                                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="42%" outerRadius={72}>
                                            {pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={methodColor(entry.name)} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip formatter={(val) => formatMWK(val)} />
                                        <Legend verticalAlign="bottom" height={28} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {showReportBody && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                        Spend by Category
                    </Typography>
                    {loading ? (
                        <Skeleton variant="rounded" width="100%" height={200} />
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell align="right">Transactions</TableCell>
                                        <TableCell align="right">Total Spend</TableCell>
                                        <TableCell sx={{ minWidth: 140 }}>% of Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {byCategory.map((row, idx) => {
                                        const pct = totalApproved > 0 ? (Number(row.total) / totalApproved) * 100 : 0;
                                        return (
                                            <TableRow key={row.categoryId || idx}>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>{row.categoryName ?? "—"}</TableCell>
                                                <TableCell align="right">{row.count ?? 0}</TableCell>
                                                <TableCell align="right">{formatMWK(row.total)}</TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {pct.toFixed(1)}%
                                                        </Typography>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.min(100, pct)}
                                                            sx={{ height: 4, borderRadius: 1, maxWidth: 200 }}
                                                        />
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow sx={{ "& td": { fontWeight: 700, borderTop: (t) => `2px solid ${t.palette.divider}` } }}>
                                        <TableCell colSpan={2}>Total</TableCell>
                                        <TableCell align="right">{totalTx}</TableCell>
                                        <TableCell align="right">{formatMWK(totalCatSpend)}</TableCell>
                                        <TableCell>
                                            {totalApproved > 0 ? (
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {((totalCatSpend / totalApproved) * 100).toFixed(1)}%
                                                    </Typography>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(100, (totalCatSpend / totalApproved) * 100)}
                                                        sx={{ height: 4, borderRadius: 1, maxWidth: 200 }}
                                                    />
                                                </Stack>
                                            ) : (
                                                "—"
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default ExpenseSummary;
